import slugify from "@sindresorhus/slugify";

declare const slugBrand: unique symbol;

export type Slug = string & { readonly [slugBrand]: true };

// `@sindresorhus/slugify` is inherently ASCII-safe: it strips real
// emoji (🎉, 👋, 🚀), romanizes Cyrillic and Arabic, folds Latin
// diacritics and ligatures (`Æ`, `ß`, `Ø`, German `ü` → `ue`), and
// lets emails slug sensibly (`alice@example.com` →
// `alice-example-com`, not `aliceexamplecom`).
//
// `decamelize: false` disables the library's default of splitting on
// internal capital letters — we don't want `iPhone` → `i-phone`,
// `3B` → `3-b`, or `McTest` → `mc-test`, as those match common brand
// and name conventions that users expect to survive slugification.
//
// The library ships three "opinionated" overridable replacements
// (see `@sindresorhus/slugify/overridable-replacements.js`):
//   `&` → ` and `, `🦄` → ` unicorn `, `♥` → ` love `.
// We override all three to empty so the rule is uniform: anything
// non-alphanumeric disappears. This also matches the pre-existing
// Postgres trigger behavior for `&`, keeping production slugs stable.
const OVERRIDABLE_REPLACEMENTS: [string, string][] = [
  ["&", ""],
  ["🦄", ""],
  ["♥", ""],
];

export function slugifyName(name: string): Slug {
  return slugify(name, {
    decamelize: false,
    customReplacements: OVERRIDABLE_REPLACEMENTS,
  }) as Slug;
}

export function personalSpaceSlugForEmail(email: string): Slug {
  const local = email.split("@")[0] ?? "";
  const fromLocal = slugifyName(local);
  return (fromLocal.length > 0 ? fromLocal : slugifyName(email)) as Slug;
}

export function nameSluggable(name: string): boolean {
  return slugifyName(name).length > 0;
}

// A slug is a subdomain label (`<slug>.<domain>`), so a user-supplied one must
// be a valid DNS label: lowercase alphanumerics separated by single hyphens,
// starting and ending alphanumeric, at most 63 characters. `_` is disallowed,
// so reserved infrastructure prefixes like `__3b` can never pass regardless of
// the denylist.
export const MAX_SLUG_LENGTH = 63;

export const RESERVED_SLUGS: ReadonlySet<string> = new Set(["www", "api"]);

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// The single source of truth for why a slug is rejected, so the UI, the Zod
// field, and the mutators all show the same reason. Returns null when valid.
export function slugValidationError(slug: string): string | null {
  if (slug.length === 0) return "Enter a space URL.";
  if (slug.length > MAX_SLUG_LENGTH)
    return `A space URL must be ${MAX_SLUG_LENGTH} characters or less.`;
  if (!SLUG_PATTERN.test(slug))
    return "Use lowercase letters, digits, and single hyphens, starting and ending with a letter or digit.";
  if (RESERVED_SLUGS.has(slug)) return "This space URL is reserved.";
  return null;
}

export function isValidSlug(slug: string): slug is Slug {
  return slugValidationError(slug) === null;
}
